import React, { useState, useEffect } from "react";
import "./PageStyles.css";

const CodingTutorialsPage = () => {
  const [tutorials, setTutorials] = useState([]);
  const [selectedTutorial, setSelectedTutorial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const apiUrl = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const fetchTutorials = async () => {
      try {
        const response = await fetch(`${apiUrl}/tutorials`);
        if (!response.ok) {
          throw new Error(
            `Failed to fetch: ${response.status} ${response.statusText}`
          );
        }
        const data = await response.json();
        const parsedData = typeof data.body === 'string' ? JSON.parse(data.body) : data;
        setTutorials(Array.isArray(parsedData) ? parsedData : []);
      } catch (e) {
        setError(e.message);
        console.error("Failed to fetch tutorials:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchTutorials();
  }, [apiUrl]);

  const extractArticleContent = (html) => {
    if (!html) return "";
    
    let content = html;
    // Extract content within <body> tags if they exist
    const bodyMatch = content.match(/<body[^>]*>([\s\S]*)<\/body>/i);
    if (bodyMatch) {
      content = bodyMatch[1].trim();
    }

    // Strip the first <h1> match to avoid double titles (frontend already shows the title)
    content = content.replace(/<h1[^>]*>[\s\S]*?<\/h1>/i, "").trim();
    
    return content;
  };

  const handleTutorialClick = async (tutorialId) => {
    setLoading(true);
    try {
      const response = await fetch(`${apiUrl}/tutorials/${tutorialId}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch tutorial metadata: ${response.status}`);
      }
      const data = await response.json();
      
      // If we have a content_url (Presigned S3 URL), fetch the actual content
      if (data.content_url) {
        try {
          const contentResponse = await fetch(data.content_url);
          if (!contentResponse.ok) {
            throw new Error(`Failed to fetch content from storage: ${contentResponse.status}`);
          }
          data.content = await contentResponse.text();
        } catch (contentError) {
          console.error("Error fetching content from S3:", contentError);
          data.content = "<p>Error loading tutorial content from storage.</p>";
        }
      }

      // Safeguard: Clean content in the frontend
      if (data && data.content) {
        data.content = extractArticleContent(data.content);
      }

      setSelectedTutorial(data);
    } catch (e) {
      console.error("Failed to fetch single tutorial:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setSelectedTutorial(null);
  };

  if (loading && !selectedTutorial) {
    return (
      <div className="page-container">
        <p>Loading tutorials...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <p>Error fetching tutorials: {error}</p>
      </div>
    );
  }

  if (selectedTutorial) {
    return (
      <div className="page-container">
        <button onClick={handleBack} className="back-btn">
          &larr; Back to Tutorials
        </button>
        <article className="full-article">
          <h2>{selectedTutorial.title}</h2>
          <span className="article-date">
            Published on: {new Date(selectedTutorial.published_at).toLocaleDateString()}
          </span>
          <div
            className="article-content"
            dangerouslySetInnerHTML={{ __html: selectedTutorial.content }}
          />
        </article>
      </div>
    );
  }

  return (
    <div className="page-container">
      <h2>Coding Tutorials</h2>
      <p>
        Step-by-step guides to help you master programming concepts.
      </p>
      <div className="content-grid">
        {tutorials && tutorials.length > 0 ? (
          tutorials.map((tutorial) => (
            <div key={tutorial.tutorial_id} className="content-card">
              <h3>{tutorial.title}</h3>
              <p>{tutorial.summary}</p>
              <span>
                Published on: {new Date(tutorial.published_at).toLocaleDateString()}
              </span>
              <button
                className="read-more-btn"
                onClick={() => handleTutorialClick(tutorial.tutorial_id)}
              >
                Read More
              </button>
            </div>
          ))
        ) : (
          <p>No tutorials found.</p>
        )}
      </div>
    </div>
  );
};

export default CodingTutorialsPage;
